import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Lang = 'ar' | 'en';

interface LanguageContextType {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
  dir: 'rtl' | 'ltr';
}

const translations: Record<Lang, Record<string, string>> = {
  ar: {
    appName: 'DeliveryJo',
    dashboard: 'لوحة التحكم',
    shipments: 'الشحنات',
    addShipment: 'إضافة شحنة',
    editShipment: 'تعديل الشحنة',
    trackingNumber: 'رقم التتبع',
    referenceNumber: 'الرقم المرجعي',
    senderName: 'المرسل',
    senderPhone: 'هاتف المرسل',
    senderAddress: 'عنوان المرسل',
    receiverName: 'المستلم',
    receiverPhone: 'الهاتف',
    receiverAddress: 'العنوان',
    address: 'العنوان',
    phone: 'رقم الهاتف',
    description: 'الوصف',
    weight: 'الوزن (كجم)',
    pieces: 'عدد القطع',
    notes: 'ملاحظات',
    status: 'الحالة',
    preparing: 'قيد التجهيز',
    in_transit: 'في الطريق',
    delivered: 'تم التسليم',
    actions: 'إجراءات',
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    view: 'عرض',
    search: 'بحث بالرقم أو الاسم...',
    all: 'الكل',
    totalShipments: 'إجمالي الشحنات',
    recentShipments: 'آخر الشحنات',
    shipmentDetails: 'تفاصيل الشحنة',
    statusHistory: 'سجل الحالات',
    noShipments: 'لا توجد شحنات',
    confirmDelete: 'هل أنت متأكد من حذف هذه الشحنة؟',
    shipmentAdded: 'تمت إضافة الشحنة بنجاح',
    shipmentUpdated: 'تم تحديث الشحنة بنجاح',
    shipmentDeleted: 'تم حذف الشحنة بنجاح',
    filterByStatus: 'فلترة حسب الحالة',
    back: 'رجوع',
    createdAt: 'تاريخ الإنشاء',
    updatedAt: 'آخر تحديث',
    changeStatus: 'تغيير الحالة',
    overview: 'نظرة عامة',
    required: 'هذا الحقل مطلوب',
    shipmentInfo: 'بيانات الشحنة',
    senderInfo: 'بيانات المرسل',
    receiverInfo: 'بيانات المستلم',
    deliveryAddress: 'عنوان التسليم',
    city: 'المدينة ',
    region: 'المنطقة',
    statusChanged: 'تم تغيير حالة الشحنة',
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    loginSubtitle: 'سجل دخولك لإدارة الشحنات',
    loginError: 'اسم المستخدم أو كلمة المرور غير صحيحة',
    rememberMe: 'تذكرني',
    contactUs: 'اتصل بنا',
    passwordMin: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
    resetPassword: 'إعادة تعيين كلمة المرور',
    newPassword: 'كلمة المرور الجديدة',
    passwordResetSuccess: 'تم تغيير كلمة المرور بنجاح',
    cannotDeleteSelf: 'لا يمكنك حذف حسابك',
    errorLoadingUsers: 'حدث خطأ في تحميل المستخدمين',
    errorSavingUser: 'حدث خطأ في حفظ المستخدم',
    settings: 'الإعدادات',
    statusManagement: 'إدارة الحالات',
    userManagement: 'إدارة المستخدمين',
    addStatus: 'إضافة حالة',
    editStatus: 'تعديل الحالة',
    statusKey: 'مفتاح الحالة',
    nameAr: 'الاسم بالعربية',
    nameEn: 'الاسم بالإنجليزية',
    color: 'اللون',
    statusAdded: 'تمت إضافة الحالة بنجاح',
    statusUpdated: 'تم تحديث الحالة بنجاح',
    statusDeleted: 'تم حذف الحالة بنجاح',
    confirmDeleteStatus: 'هل أنت متأكد من حذف هذه الحالة؟',
    minOneStatus: 'يجب أن تبقى حالة واحدة على الأقل',
    addUser: 'إضافة مستخدم',
    editUser: 'تعديل المستخدم',
    userName: 'اسم الموظف',
    email: 'البريد الإلكتروني',
    role: 'الصلاحية',
    activeStatus: 'الحالة',
    userAdded: 'تمت إضافة المستخدم بنجاح',
    userUpdated: 'تم تحديث المستخدم بنجاح',
    userDeleted: 'تم حذف المستخدم بنجاح',
    confirmDeleteUser: 'هل أنت متأكد من حذف هذا المستخدم؟',
    noUsers: 'لا يوجد مستخدمون',
    drivers: 'السائقين',
    customers: 'العملاء',
    customerManagement: 'إدارة العملاء',
    addCustomer: 'إضافة عميل',
    editCustomer: 'تعديل العميل',
    customerName: 'اسم العميل',
    customerCity: 'مدينة العميل',
    customerAdded: 'تمت إضافة العميل بنجاح',
    customerUpdated: 'تم تحديث العميل بنجاح',
    customerDeleted: 'تم حذف العميل بنجاح',
    confirmDeleteCustomer: 'هل أنت متأكد من حذف هذا العميل؟',
    noCustomers: 'لا يوجد عملاء',
    searchCustomers: 'بحث بالاسم أو الهاتف...',
    driverManagement: 'إدارة السائقين',
    addDriver: 'إضافة سائق',
    editDriver: 'تعديل السائق',
    driverName: 'اسم السائق',
    driverFullName: 'الاسم الرباعي',
    driverResidence: 'مكان الإقامة',
    driverAddress: 'العنوان التفصيلي',
    driverPhone: 'رقم الهاتف',
    driverNationalId: 'رقم الوثيقة / الهوية',
    driverNationalNumber: 'الرقم الوطني',
    vehicleNumber: 'رقم اللوحة',
    vehicleType: 'نوع المركبة',
    vehicleColor: 'لون المركبة',
    referenceName: 'اسم المعرّف',
    referencePhone: 'هاتف المعرّف',
    referenceAddress: 'عنوان المعرّف',
    driverPhoto: 'الصورة الشخصية',
    vehicleInfo: 'معلومات المركبة',
    referenceInfo: 'معلومات المعرّف',
    uploadPhoto: 'رفع صورة',
    changePhoto: 'تغيير الصورة',
    driverAdded: 'تمت إضافة السائق بنجاح',
    driverUpdated: 'تم تحديث بيانات السائق بنجاح',
    driverDeleted: 'تم حذف السائق بنجاح',
    confirmDeleteDriver: 'هل أنت متأكد من حذف هذا السائق؟',
    noDrivers: 'لا يوجد سائقون',
    selectSender: 'اختر المرسل',
    noCustomersYet: 'لا يوجد عملاء - أضف عملاء أولاً',
    returnStatus: 'حالة المرتجع',
    returnManagement: 'إدارة حالة المرتجع',
    noReturnStatus: 'لا يوجد',
    addReturnStatus: 'إضافة حالة مرتجع',
    editReturnStatus: 'تعديل حالة المرتجع',
    returnStatusAdded: 'تمت إضافة حالة المرتجع بنجاح',
    returnStatusUpdated: 'تم تحديث حالة المرتجع بنجاح',
    returnStatusDeleted: 'تم حذف حالة المرتجع بنجاح',
    confirmDeleteReturnStatus: 'هل أنت متأكد من حذف هذه الحالة؟',
    minOneReturnStatus: 'يجب أن تبقى حالة واحدة على الأقل',
    noReturnStatuses: 'لا توجد حالات مرتجع',
    branchManagement: 'إدارة الأفرع',
    addBranch: 'إضافة فرع',
    editBranch: 'تعديل الفرع',
    branchName: 'اسم الفرع',
    branchAdded: 'تمت إضافة الفرع بنجاح',
    branchUpdated: 'تم تحديث الفرع بنجاح',
    branchDeleted: 'تم حذف الفرع بنجاح',
    confirmDeleteBranch: 'هل أنت متأكد من حذف هذا الفرع؟',
    minOneBranch: 'يجب أن يبقى فرع واحد على الأقل',
    noBranches: 'لا توجد أفرع',
    branch: 'الفرع',
    selectBranch: 'اختر الفرع',
    shipmentValue: 'قيمة الشحنة',
    deliveryFee: 'أجور التوصيل',
    filters: 'فلاتر',
    filterByDriver: 'حسب السائق',
    filterByBranch: 'حسب الفرع',
    filterByCustomer: 'حسب العميل',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    selectDriver: 'اختر السائق',
    selectCustomer: 'اختر العميل',
    clearFilters: 'مسح الفلاتر',
    driver: 'السائق',
    customer: 'العميل',
    countryManagement: 'إدارة البلدان',
    country: 'البلد',
    countryName: 'اسم البلد',
    cities: 'المدن',
    cityName: 'اسم المدينة',
    addCity: 'إضافة مدينة',
    addCountry: 'إضافة بلد',
    deleteCountry: 'حذف البلد',
    confirmDeleteCountry: 'هل أنت متأكد من حذف هذا البلد وجميع مدنه؟',
    countryAdded: 'تمت إضافة البلد بنجاح',
    countryDeleted: 'تم حذف البلد بنجاح',
    cityAdded: 'تمت إضافة المدينة بنجاح',
    cityDeleted: 'تم حذف المدينة بنجاح',
    noCountries: 'لا توجد بلدان',
    noCities: 'لا توجد مدن',
    enabledCountry: 'مفعّل',
    selectCity: 'اختر المدينة',
    senderCity: 'مدينة العميل',
    receiverCity: 'العنوان',
    deliveryDateFrom: 'تاريخ الاستلام من',
    deliveryDateTo: 'تاريخ الاستلام الى',
    entry: 'الادخال',
    singleEntry: 'ادخال شحنة',
    bulkEntry: 'ادخال متعدد',
    humanAudit: 'تدقيق بشري',
    comingSoon: 'قريباً...',
    autoGenerated: 'تلقائي إذا ترك فارغاً',
    followUp: 'المتابعة',
    processing: 'المعالجة',
    customerService: 'خدمة العملاء',
    pendingIssues: 'اغلاق ومطابقة الشحنات',
    coordinationReport: 'كشف بيانات التنسيق',
    printExcel: 'طباعة Excel',
    printPdf: 'طباعة PDF',
    bulkPrintExcel: 'طباعة Excel للمحدد',
    customerPhone: 'هاتف العميل',
    shipmentId: 'رقم الشحنة',
    selectColumns: 'الأعمدة المرئية',
    fixedNotes: 'الملاحظات الثابتة',
    backBtn: 'رجوع',
    accounting: 'المحاسبة',
    driverAccounting: 'محاسبة سائق',
    customerAccounting: 'صرف للعميل',
    auditAccounts: 'تدقيق حسابات',
    dailyStatement: 'كشف يومي',
    aiEntry: 'إدخال بالذكاء الاصطناعي',
    uploadImages: 'ارفع صور البوالص',
    dragDropOrClick: 'اسحب وأفلت الصور هنا أو انقر لاختيار الملفات (يدعم ZIP)',
    selectFiles: 'اختر ملفات',
    processingImages: 'جاري معالجة الصور...',
    completed: 'مكتمل',
    reviewingItem: 'مراجعة العنصر',
    confirmAndAdd: 'تأكيد وإضافة',
    skip: 'تخطي',
    exportExcel: 'تصدير Excel',
    addToSystem: 'إضافة للنظام',
    newSession: 'جلسة جديدة',
    exportFileName: 'اسم ملف التصدير',
    shipmentsAddedToSystem: 'تمت إضافة الشحنات للنظام بنجاح',
    allImagesFailed: 'فشلت معالجة جميع الصور',
    noConfirmedItems: 'لم يتم تأكيد أي عنصر',
    aiAssistant: 'مساعد Logi الذكي',
    typeMessage: 'اكتب رسالتك...',
    sendMessage: 'إرسال',
    aiWelcome: 'مرحباً! أنا مساعد Logi الذكي. كيف يمكنني مساعدتك في إدارة الشحنات؟',
    messaging: 'المراسلة',
    chats: 'محادثات',
    groups: 'WhatsAppLogi',
    departments: 'أقسام',
    newGroup: 'مجموعة جديدة',
    newChat: 'محادثة جديدة',
    groupName: 'اسم المجموعة',
    addMembers: 'أضف أعضاء',
    noMessages: 'لا توجد رسائل',
    joinDepartment: 'انضمام',
    leaveDepartment: 'مغادرة',
    searchContacts: 'بحث في جهات الاتصال...',
    banned: 'محظور',
    active: 'نشط',
    groupInfo: 'معلومات المجموعة',
    groupSettings: 'إعدادات المجموعة',
    newDepartment: 'إضافة قسم جديد',
    deptNamePlaceholder: 'اسم القسم',
    creatingDept: 'جاري الإنشاء...',
    createDept: 'إنشاء القسم',
    accountPermissions: 'إدارة الحسابات وصلاحياتها',
    definitionsManagement: 'إدارة التعريفات',
    driverApp: 'تطبيق السائق',
    daShipmentsList: 'الشحنات',
    daFinancial: 'المالية',
    daMap: 'الخريطة',
    daPending: 'قيد التوصيل',
    daFailed: 'فشل',
    daFailedPostponed: 'تعذر ومؤجل',
    daFinancialSummary: 'الملخص المالي للجولة',
    daDeliveredShipments: 'الشحنات المسلّمة',
    daTotalValue: 'إجمالي قيمة الشحنات',
    daTotalFees: 'إجمالي أجور التوصيل',
     daTotalCollected: 'إجمالي المبلغ المحصّل',
    daDriverFee: 'أجور السائق',
    daTotalRequired: 'إجمالي المطلوب',
    daDeliveryMap: 'خريطة التوصيل',
    daMapComingSoon: 'خريطة التوصيل التفاعلية ستتوفر قريباً',
    daDeliveryAddresses: 'عناوين التوصيل المتبقية',
    customerApp: 'تطبيق العميل',
    caShipmentsList: 'شحناتي',
    caFinancial: 'الملخص المالي',
    caStatusLog: 'سجل الحالات',
    caNoUpdates: 'لا توجد تحديثات',
    caFinancialSummary: 'الملخص المالي',
    caTotalValue: 'إجمالي قيم الشحنات',
    caTotalFees: 'إجمالي أجور التوصيل',
    caCollected: 'المبلغ المحصّل',
    caAmountDue: 'المستحق للعميل',
    scannerInput: 'إدخال الماسح الضوئي',
    scanPlaceholder: 'امسح رقم التتبع...',
    execute: 'تنفيذ',
    bulkActions: 'العمليات الجماعية',
    assignToDriver: 'نقل لسائق',
    selectAction: 'اختر العملية',
    selectedShipments: 'الشحنات المحددة',
    clearSelection: 'مسح التحديد',
    trackingNotFound: 'رقم التتبع غير موجود',
    bulkActionSuccess: 'تم تنفيذ العملية بنجاح',
    alreadyScanned: 'تم مسح هذا الرقم مسبقاً',
    changeReturnStatus: 'تغيير حالة المرتجع',
    confirmExecution: 'تأكيد التنفيذ',
    selectValue: 'اختر القيمة',
    permissionsManagement: 'إدارة الصلاحيات',
    screenAccess: 'الوصول للشاشات',
    actionPermissions: 'صلاحيات العمليات',
    resetToDefaults: 'حفظ كافتراضي',
    permissionsSaved: 'تم حفظ الصلاحيات بنجاح',
    permissionsReset: 'تم حفظ الإعدادات كافتراضية',
    dispatch: 'المستودع',
    receive: 'استلام',
    confirmReceive: 'تأكيد التجهيز',
    scanReturns: 'مسح المرتجعات',
    selectReturnStatus: 'اختر حالة المرتجع',
    noScannedShipments: 'لا توجد شحنات ممسوحة',
    receiveSuccess: 'تم تحديث حالة الشحنة بنجاح',
    scanReturnPlaceholder: 'امسح رقم التتبع للمرتجع...',
    removeFromList: 'إزالة',
    bundles: 'الحزم',
    createBundle: 'إنشاء حزمة',
    bundleName: 'اسم الحزمة',
    bundleCreated: 'تم إنشاء الحزمة بنجاح',
    bundleDestination: 'المستلم',
    bundleStatus: 'الحالة',
    bundleInTransit: 'قيد النقل',
    bundleAccepted: 'تم القبول',
    bundleRejected: 'مرفوضة',
    bundleShipments: 'شحنات الحزمة',
    acceptBundle: 'قبول',
    rejectBundle: 'رفض',
    noBundles: 'لا توجد حزم',
    bundleNameRequired: 'يرجى إدخال اسم الحزمة',
    awaitingDriver: 'بانتظار موافقة الناقل',
    bundleReceipt: 'سند نقل',
    printBundleReceipt: 'طباعة سند النقل',
    receiveBundleBtn: 'استلام حزمة',
    scanBundleTracking: 'امسح رقم تتبع الحزمة',
    scanShipmentsOptional: 'مسح الشحنات (اختياري)',
    skipScan: 'تخطي',
    bundleAcceptedByDriver: 'تم قبول الحزمة من الناقل',
    scanToAcceptBundle: 'امسح رقم تتبع الحزمة لقبولها',
  },
  en: {
    appName: 'DeliveryJo',
    dashboard: 'Dashboard',
    shipments: 'Shipments',
    addShipment: 'Add Shipment',
    editShipment: 'Edit Shipment',
    trackingNumber: 'Tracking Number',
    referenceNumber: 'Reference Number',
    senderName: 'Sender',
    senderPhone: 'Sender Phone',
    senderAddress: 'Sender Address',
    receiverName: 'Receiver Name',
    receiverPhone: 'Receiver Phone',
    receiverAddress: 'Receiver Address',
    address: 'Address',
    phone: 'Phone',
    description: 'Description',
    weight: 'Weight (kg)',
    pieces: 'Pieces',
    notes: 'Notes',
    status: 'Status',
    preparing: 'Preparing',
    in_transit: 'In Transit',
    delivered: 'Delivered',
    actions: 'Actions',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    search: 'Search by number or name...',
    all: 'All',
    totalShipments: 'Total Shipments',
    recentShipments: 'Recent Shipments',
    shipmentDetails: 'Shipment Details',
    statusHistory: 'Status History',
    noShipments: 'No shipments found',
    confirmDelete: 'Are you sure you want to delete this shipment?',
    shipmentAdded: 'Shipment added successfully',
    shipmentUpdated: 'Shipment updated successfully',
    shipmentDeleted: 'Shipment deleted successfully',
    filterByStatus: 'Filter by status',
    back: 'Back',
    createdAt: 'Created At',
    updatedAt: 'Last Updated',
    changeStatus: 'Change Status',
    overview: 'Overview',
    required: 'This field is required',
    shipmentInfo: 'Shipment Info',
    senderInfo: 'Sender Info',
    receiverInfo: 'Receiver Info',
    deliveryAddress: 'Delivery Address',
    city: 'Recipient City',
    region: 'Region',
    statusChanged: 'Shipment status changed',
    login: 'Login',
    logout: 'Logout',
    username: 'Username',
    password: 'Password',
    loginSubtitle: 'Sign in to manage shipments',
    loginError: 'Invalid username or password',
    rememberMe: 'Remember Me',
    contactUs: 'Contact Us',
    passwordMin: 'Password must be at least 6 characters',
    resetPassword: 'Reset Password',
    newPassword: 'New Password',
    passwordResetSuccess: 'Password changed successfully',
    cannotDeleteSelf: 'Cannot delete your own account',
    errorLoadingUsers: 'Error loading users',
    errorSavingUser: 'Error saving user',
    settings: 'Settings',
    statusManagement: 'Status Management',
    userManagement: 'User Management',
    addStatus: 'Add Status',
    editStatus: 'Edit Status',
    statusKey: 'Status Key',
    nameAr: 'Arabic Name',
    nameEn: 'English Name',
    color: 'Color',
    statusAdded: 'Status added successfully',
    statusUpdated: 'Status updated successfully',
    statusDeleted: 'Status deleted successfully',
    confirmDeleteStatus: 'Are you sure you want to delete this status?',
    minOneStatus: 'At least one status must remain',
    addUser: 'Add User',
    editUser: 'Edit User',
    userName: 'Employee Name',
    email: 'Email',
    role: 'Role',
    activeStatus: 'Active',
    userAdded: 'User added successfully',
    userUpdated: 'User updated successfully',
    userDeleted: 'User deleted successfully',
    confirmDeleteUser: 'Are you sure you want to delete this user?',
    noUsers: 'No users found',
    drivers: 'Drivers',
    customers: 'Customers',
    customerManagement: 'Customer Management',
    addCustomer: 'Add Customer',
    editCustomer: 'Edit Customer',
    customerName: 'Customer Name',
    customerCity: 'Customer City',
    customerAdded: 'Customer added successfully',
    customerUpdated: 'Customer updated successfully',
    customerDeleted: 'Customer deleted successfully',
    confirmDeleteCustomer: 'Are you sure you want to delete this customer?',
    noCustomers: 'No customers found',
    searchCustomers: 'Search by name or phone...',
    driverManagement: 'Driver Management',
    addDriver: 'Add Driver',
    editDriver: 'Edit Driver',
    driverName: 'Driver Name',
    driverFullName: 'Full Name (4 parts)',
    driverResidence: 'Place of Residence',
    driverAddress: 'Detailed Address',
    driverPhone: 'Phone Number',
    driverNationalId: 'Document / ID No.',
    driverNationalNumber: 'National Number',
    vehicleNumber: 'Plate Number',
    vehicleType: 'Vehicle Type',
    vehicleColor: 'Vehicle Color',
    referenceName: 'Reference Name',
    referencePhone: 'Reference Phone',
    referenceAddress: 'Reference Address',
    driverPhoto: 'Profile Photo',
    vehicleInfo: 'Vehicle Information',
    referenceInfo: 'Reference Person',
    uploadPhoto: 'Upload Photo',
    changePhoto: 'Change Photo',
    driverAdded: 'Driver added successfully',
    driverUpdated: 'Driver updated successfully',
    driverDeleted: 'Driver deleted successfully',
    confirmDeleteDriver: 'Are you sure you want to delete this driver?',
    noDrivers: 'No drivers found',
    selectSender: 'Select Sender',
    noCustomersYet: 'No customers yet - add customers first',
    returnStatus: 'Return Status',
    returnManagement: 'Return Status Management',
    noReturnStatus: 'None',
    addReturnStatus: 'Add Return Status',
    editReturnStatus: 'Edit Return Status',
    returnStatusAdded: 'Return status added successfully',
    returnStatusUpdated: 'Return status updated successfully',
    returnStatusDeleted: 'Return status deleted successfully',
    confirmDeleteReturnStatus: 'Are you sure you want to delete this return status?',
    minOneReturnStatus: 'At least one return status must remain',
    noReturnStatuses: 'No return statuses found',
    branchManagement: 'Branch Management',
    addBranch: 'Add Branch',
    editBranch: 'Edit Branch',
    branchName: 'Branch Name',
    branchAdded: 'Branch added successfully',
    branchUpdated: 'Branch updated successfully',
    branchDeleted: 'Branch deleted successfully',
    confirmDeleteBranch: 'Are you sure you want to delete this branch?',
    minOneBranch: 'At least one branch must remain',
    noBranches: 'No branches found',
    branch: 'Branch',
    selectBranch: 'Select Branch',
    shipmentValue: 'Shipment Value',
    deliveryFee: 'Delivery Fee',
    filters: 'Filters',
    filterByDriver: 'By Driver',
    filterByBranch: 'By Branch',
    filterByCustomer: 'By Customer',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    selectDriver: 'Select Driver',
    selectCustomer: 'Select Customer',
    clearFilters: 'Clear Filters',
    driver: 'Driver',
    customer: 'Customer',
    countryManagement: 'Country Management',
    country: 'Country',
    countryName: 'Country Name',
    cities: 'Cities',
    cityName: 'City Name',
    addCity: 'Add City',
    addCountry: 'Add Country',
    deleteCountry: 'Delete Country',
    confirmDeleteCountry: 'Are you sure you want to delete this country and all its cities?',
    countryAdded: 'Country added successfully',
    countryDeleted: 'Country deleted successfully',
    cityAdded: 'City added successfully',
    cityDeleted: 'City deleted successfully',
    noCountries: 'No countries found',
    noCities: 'No cities found',
    enabledCountry: 'Enabled',
    selectCity: 'Select City',
    senderCity: 'Sender City',
    receiverCity: 'Receiver City',
    deliveryDateFrom: 'Delivery Date From',
    deliveryDateTo: 'Delivery Date To',
    entry: 'Entry',
    singleEntry: 'Single Entry',
    bulkEntry: 'Bulk Entry',
    humanAudit: 'Human Audit',
    comingSoon: 'Coming Soon...',
    autoGenerated: 'Auto-generated if left empty',
    followUp: 'Follow Up',
    processing: 'Processing',
    customerService: 'Customer Service',
    pendingIssues: 'اغلاق ومطابقة الشحنات',
    coordinationReport: 'Coordination Report',
    printExcel: 'Print Excel',
    printPdf: 'Print PDF',
    bulkPrintExcel: 'Print Excel for Selected',
    customerPhone: 'Customer Phone',
    shipmentId: 'Shipment ID',
    selectColumns: 'Visible Columns',
    fixedNotes: 'Fixed Notes',
    backBtn: 'Back',
    accounting: 'Accounting',
    driverAccounting: 'Driver Accounting',
    customerAccounting: 'Disburse to Customer',
    auditAccounts: 'Audit Accounts',
    dailyStatement: 'Daily Statement',
    aiEntry: 'AI Entry',
    uploadImages: 'Upload Waybill Images',
    dragDropOrClick: 'Drag & drop images here or click to select files (ZIP supported)',
    selectFiles: 'Select Files',
    processingImages: 'Processing images...',
    completed: 'completed',
    reviewingItem: 'Reviewing item',
    confirmAndAdd: 'Confirm & Add',
    skip: 'Skip',
    exportExcel: 'Export Excel',
    addToSystem: 'Add to System',
    newSession: 'New Session',
    exportFileName: 'Export file name',
    shipmentsAddedToSystem: 'Shipments added to system successfully',
    allImagesFailed: 'All images failed to process',
    noConfirmedItems: 'No items were confirmed',
    aiAssistant: 'Logi AI Assistant',
    typeMessage: 'Type your message...',
    sendMessage: 'Send',
    aiWelcome: 'Hello! I\'m Logi AI Assistant. How can I help you manage shipments?',
    messaging: 'Messaging',
    chats: 'Chats',
    groups: 'WhatsAppLogi',
    departments: 'Departments',
    newGroup: 'New Group',
    newChat: 'New Chat',
    groupName: 'Group Name',
    addMembers: 'Add Members',
    noMessages: 'No messages',
    joinDepartment: 'Join',
    leaveDepartment: 'Leave',
    searchContacts: 'Search contacts...',
    banned: 'Banned',
    active: 'Active',
    groupInfo: 'Group Info',
    groupSettings: 'Group Settings',
    newDepartment: 'Add New Department',
    deptNamePlaceholder: 'Department Name',
    creatingDept: 'Creating...',
    createDept: 'Create Department',
    accountPermissions: 'Account & Permissions',
    definitionsManagement: 'Definitions',
    driverApp: 'Driver App',
    daShipmentsList: 'Shipments',
    daFinancial: 'Financial',
    daMap: 'Map',
    daPending: 'Pending',
    daFailed: 'Failed',
    daFailedPostponed: 'Failed & Postponed',
    daFinancialSummary: 'Route Financial Summary',
    daDeliveredShipments: 'Delivered Shipments',
    daTotalValue: 'Total Shipment Value',
    daTotalFees: 'Total Delivery Fees',
    daTotalCollected: 'Total Collected',
    daDriverFee: 'Driver Fee',
    daTotalRequired: 'Total Required',
    daDeliveryMap: 'Delivery Map',
    daMapComingSoon: 'Interactive delivery map coming soon',
    daDeliveryAddresses: 'Remaining Delivery Addresses',
    customerApp: 'Customer App',
    caShipmentsList: 'My Shipments',
    caFinancial: 'Financial Summary',
    caStatusLog: 'Status Log',
    caNoUpdates: 'No updates',
    caFinancialSummary: 'Financial Summary',
    caTotalValue: 'Total Shipment Value',
    caTotalFees: 'Total Delivery Fees',
    caCollected: 'Collected Amount',
    caAmountDue: 'Amount Due to Customer',
    scannerInput: 'Scanner Input',
    scanPlaceholder: 'Scan tracking number...',
    execute: 'Execute',
    bulkActions: 'Bulk Actions',
    assignToDriver: 'Assign to Driver',
    selectAction: 'Select Action',
    selectedShipments: 'Selected Shipments',
    clearSelection: 'Clear Selection',
    trackingNotFound: 'Tracking number not found',
    bulkActionSuccess: 'Action executed successfully',
    alreadyScanned: 'Already scanned',
    changeReturnStatus: 'Change Return Status',
    confirmExecution: 'Confirm Execution',
    selectValue: 'Select Value',
    permissionsManagement: 'Permissions Management',
    screenAccess: 'Screen Access',
    actionPermissions: 'Action Permissions',
    resetToDefaults: 'Save as Default',
    permissionsSaved: 'Permissions saved successfully',
    permissionsReset: 'Settings saved as defaults',
    dispatch: 'Warehouse',
    receive: 'Receive',
    confirmReceive: 'Confirm Preparation',
    scanReturns: 'Scan Returns',
    selectReturnStatus: 'Select Return Status',
    noScannedShipments: 'No scanned shipments',
    receiveSuccess: 'Shipment status updated successfully',
    scanReturnPlaceholder: 'Scan return tracking number...',
    removeFromList: 'Remove',
    bundles: 'Bundles',
    createBundle: 'Create Bundle',
    bundleName: 'Bundle Name',
    bundleCreated: 'Bundle created successfully',
    bundleDestination: 'Recipient Name',
    bundleStatus: 'Status',
    bundleInTransit: 'In Transit',
    bundleAccepted: 'Accepted',
    bundleRejected: 'Rejected',
    bundleShipments: 'Bundle Shipments',
    acceptBundle: 'Accept',
    rejectBundle: 'Reject',
    noBundles: 'No bundles found',
    bundleNameRequired: 'Please enter a bundle name',
    awaitingDriver: 'Awaiting Driver',
    bundleReceipt: 'Transport Receipt',
    printBundleReceipt: 'Print Transport Receipt',
    receiveBundleBtn: 'Receive Bundle',
    scanBundleTracking: 'Scan bundle tracking number',
    scanShipmentsOptional: 'Scan shipments (optional)',
    skipScan: 'Skip',
    bundleAcceptedByDriver: 'Bundle accepted by driver',
    scanToAcceptBundle: 'Scan bundle tracking to accept',
  },
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    return (localStorage.getItem('lang') as Lang) || 'en';
  });

  const setLang = (l: Lang) => {
    setLangState(l);
    localStorage.setItem('lang', l);
  };

  const t = (key: string) => translations[lang][key] || key;
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [lang, dir]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, dir }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
